import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getLocalStorageItem, getUserSetting, setUserSetting } from '../utils/localStorage';
import { api } from '../utils/apiClient';
import enTranslations from './translations/en.json';
import lvTranslations from './translations/lv.json';
import deTranslations from './translations/de.json';
import zhTranslations from './translations/zh.json';

/**
 * MODULAR TRANSLATIONS
 * 
 * Feature-specific translations are stored in separate files in the locales directory:
 * - src/i18n/locales/{language}/{namespace}.json
 * 
 * Benefits:
 * - Improves performance by loading translations only when needed
 * - Keeps translation files smaller and more maintainable
 * - Allows multiple developers to work on different features
 * 
 * To add a new namespace:
 * 1. Create a new JSON file in the appropriate language folder
 * 2. Import the file below
 * 3. Add it to the resources object
 * 4. Add it to the 'ns' array in the i18n.init() configuration
 * 
 * See the README.md in src/i18n/locales for more details.
 */
// Import modularized translations
import enWithdrawals from './locales/en/withdrawals.json';
import enPerformance from './locales/en/performance.json';
import enPrivacy from './locales/en/privacy.json';
import enTerms from './locales/en/terms.json';
import enLanding from './locales/en/landing.json';
import enAbout from './locales/en/about.json';

// Import modularized translations for other languages
import lvWithdrawals from './locales/lv/withdrawals.json';
import lvPerformance from './locales/lv/performance.json';
import lvPrivacy from './locales/lv/privacy.json';
import lvTerms from './locales/lv/terms.json';
import lvLanding from './locales/lv/landing.json';
import lvAbout from './locales/lv/about.json';

import deWithdrawals from './locales/de/withdrawals.json';
import dePerformance from './locales/de/performance.json';
import dePrivacy from './locales/de/privacy.json';
import deTerms from './locales/de/terms.json';
import deLanding from './locales/de/landing.json';
import deAbout from './locales/de/about.json';

import zhWithdrawals from './locales/zh/withdrawals.json';
import zhPerformance from './locales/zh/performance.json';
import zhPrivacy from './locales/zh/privacy.json';
import zhTerms from './locales/zh/terms.json';
import zhLanding from './locales/zh/landing.json';
import zhAbout from './locales/zh/about.json';

const resources = {
  en: {
    translation: enTranslations,
    withdrawals: enWithdrawals,
    performance: enPerformance,
    privacy: enPrivacy,
    terms: enTerms,
    landing: enLanding,
    about: enAbout
  },
  lv: {
    translation: lvTranslations,
    withdrawals: lvWithdrawals,
    performance: lvPerformance,
    privacy: lvPrivacy,
    terms: lvTerms,
    landing: lvLanding,
    about: lvAbout
  },
  de: {
    translation: deTranslations,
    withdrawals: deWithdrawals,
    performance: dePerformance,
    privacy: dePrivacy,
    terms: deTerms,
    landing: deLanding,
    about: deAbout
  },
  zh: {
    translation: zhTranslations,
    withdrawals: zhWithdrawals,
    performance: zhPerformance,
    privacy: zhPrivacy,
    terms: zhTerms,
    landing: zhLanding,
    about: zhAbout
  }
};

/**
 * Initialize i18n with settings based on the current user state
 */
const initializeI18n = () => {
  const storedLanguage = getLocalStorageItem<string>('language', 'en');
  
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      lng: storedLanguage,
      fallbackLng: 'en',
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'language',
        caches: []
      },
      interpolation: {
        escapeValue: false
      },
      // Enable namespace loading
      ns: ['translation', 'withdrawals', 'performance', 'privacy', 'terms', 'landing', 'about'],
      defaultNS: 'translation'
    });
};

// Initialize i18n immediately
initializeI18n();

/**
 * Immediately try to fetch user settings when module loads
 * This ensures we load database settings as soon as possible
 */
(async function loadInitialLanguageFromServer() {
  const email = getLocalStorageItem<string>('userEmail', '');
  const token = getLocalStorageItem<string>('authToken', '');
  
  if (email && token) {
    try {
      const response = await api.user.getSettings(email);
      if (response.success && response.settings && response.settings.language) {
        const serverLanguage = response.settings.language;
        i18n.changeLanguage(serverLanguage);
      }
    } catch (error) {
      // Silent fail, will fall back to localStorage
    }
  }
})();

/**
 * Update language based on user state (logged in or not)
 * This function checks both localStorage and server database
 */
const updateLanguageFromUserState = async () => {
  const email = getLocalStorageItem<string>('userEmail', '');
  const token = getLocalStorageItem<string>('authToken', '');
  const defaultLanguage = getLocalStorageItem<string>('language', 'en');
  
  if (email && token) {
    try {
      // First try to get language from server
      const response = await api.user.getSettings(email);
      if (response.success && response.settings) {
        // Use server setting
        const serverLanguage = response.settings.language;
        if (serverLanguage && serverLanguage !== i18n.language) {
          // Also update localStorage for consistency
          setUserSetting(email, 'language', serverLanguage);
          i18n.changeLanguage(serverLanguage);
          return;
        }
      }
    } catch (error) {
      // Silent fail, will fall back to localStorage
    }
    
    // Fall back to localStorage if server request fails
    const userLanguage = getUserSetting<string>(email, 'language', defaultLanguage);
    if (userLanguage !== i18n.language) {
      i18n.changeLanguage(userLanguage);
      
      // Try to sync localStorage setting to server
      try {
        await api.user.updateSettings(email, { language: userLanguage });
      } catch (error) {
        // Silent fail
      }
    }
  } else {
    // User is logged out, use default
    if (defaultLanguage !== i18n.language) {
      i18n.changeLanguage(defaultLanguage);
    }
  }
};

/**
 * Update language with a specific value and save to server if logged in
 * @param language The language code to set
 * @param email User's email or null if not logged in
 */
export const updateLanguage = async (language: string, email: string | null) => {
  // Update i18n language
  i18n.changeLanguage(language);
  
  // If user is logged in, update server setting
  if (email) {
    const token = getLocalStorageItem<string>('authToken', '');
    if (token) {
      try {
        await api.user.updateSettings(email, { language });
      } catch (error) {
        // Silent fail
      }
    }
  }
};

// Export the i18n instance
export default i18n;

// Also export the update function
export { updateLanguageFromUserState };

// Set up event listeners if in browser environment
if (typeof window !== 'undefined') {
  // Use a common function for all events that should trigger language updates
  const handleLanguageSettingsChange = () => {
    localStorage.removeItem('i18nextLng'); // Clear i18next cache
    // We need to run the async function, but can't await it in the event handler
    updateLanguageFromUserState().catch(() => {
      // Silent fail
    });
  };
  
  // Add event listeners for all relevant events
  window.addEventListener('localStorageConsentChanged', handleLanguageSettingsChange);
  window.addEventListener('userLoggedOut', handleLanguageSettingsChange);
  window.addEventListener('userLoggedIn', handleLanguageSettingsChange);
} 